"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

export type CategoryChipItem = { id: number; name: string };

type Props = {
  categories?: CategoryChipItem[] | null;
  label?: string;
  maxVisible?: number;
  showRestChip?: boolean;
  enableTooltip?: boolean;
  enableTitleAttr?: boolean;
  testIdPrefix?: string;
  className?: string;
};

export const CategoryChips: React.FC<Props> = ({
  categories,
  label = "Category",
  maxVisible = 2,
  showRestChip = true,
  enableTooltip = true,
  enableTitleAttr = true,
  testIdPrefix = "category-chips",
  className = "",
}) => {
  const list = useMemo(() => (categories ?? []).filter(Boolean), [categories]);

  const shown = useMemo(() => list.slice(0, maxVisible), [list, maxVisible]);
  const rest = useMemo(() => list.length - shown.length, [list.length, shown.length]);

  const allLabel = useMemo(() => list.map((c) => c.name).join(", "), [list]);

  const [open, setOpen] = useState(false);

  // ★追加：閉じた直後の “合成 click” を吸うためのフラグ
  const [blockTap, setBlockTap] = useState(false);
  const blockTimerRef = useRef<number | null>(null);

  const wrapRef = useRef<HTMLSpanElement | null>(null);

  const startBlock = () => {
    setBlockTap(true);
    if (blockTimerRef.current) window.clearTimeout(blockTimerRef.current);
    blockTimerRef.current = window.setTimeout(() => {
      setBlockTap(false);
      blockTimerRef.current = null;
    }, 220); // 150〜250ms で調整可
  };

  const closeOnly = () => {
    // ★先にブロック開始 → その後 open を閉じる
    startBlock();
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      if (blockTimerRef.current) window.clearTimeout(blockTimerRef.current);
    };
  }, []);

  // ✅ Hook 後に return（これが rules-of-hooks 対策の本体）
  if (list.length === 0) return null;

  return (
    <div
      className={`mt-2 flex flex-wrap items-center gap-1.5 ${className}`}
      title={enableTitleAttr ? `${label}: ${allLabel}` : undefined}
      data-testid={`${testIdPrefix}-root`}
    >
      {shown.map((c) => (
        <span
          key={c.id}
          className="
            inline-flex items-center
            px-2 py-0.5 rounded-full
            text-[11px] leading-4
            bg-white/10 border border-white/15
            text-white/70
          "
          data-testid={`${testIdPrefix}-chip`}
        >
          {c.name}
        </span>
      ))}

      {showRestChip && rest > 0 && (
        <span ref={wrapRef} className="group relative inline-flex items-center">
          <button
            type="button"
            className="
              inline-flex items-center
              px-2 py-0.5 rounded-full
              text-[11px] leading-4
              bg-white/5 border border-white/15
              text-white/60
            "
            aria-expanded={open}
            aria-label={`${label} +${rest}`}
            data-testid={`${testIdPrefix}-rest`}
            onPointerDown={(e) => {
              // a の「押した瞬間反応」も止める
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            +{rest}
          </button>

          {/* ★ open中 or blockTap中は全画面でタップを吸う */}
          {(open || blockTap) && (
            <div
              className="fixed inset-0 z-[9998]"
              aria-hidden="true"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeOnly(); // 閉じるだけ（遷移させない）
              }}
              onClick={(e) => {
                // 合成clickもここで潰す
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          )}

          {enableTooltip && (
            <div
              className={[
                "absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[9999]",
                "w-[min(320px,calc(100vw-24px))] min-w-[220px] max-w-[320px]",
                "rounded-xl border border-white/15",
                "bg-black/90 backdrop-blur px-3 py-2 shadow-lg",
                "pointer-events-auto",
                open ? "block" : "hidden group-hover:block group-focus-within:block",
              ].join(" ")}
              role="tooltip"
              data-testid={`${testIdPrefix}-tooltip`}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="text-[11px] text-white/60 mb-1">{label}</div>
              <div className="flex flex-wrap gap-1.5">
                {list.map((c) => (
                  <span
                    key={c.id}
                    className="
                      inline-flex items-center
                      px-2 py-0.5 rounded-full
                      text-[11px] leading-4
                      bg-white/10 border border-white/15
                      text-white/75
                    "
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </span>
      )}
    </div>
  );
};